import React, { useState, useEffect } from "react";
import ClaimsOrderSelection from "./ClaimsOrderSelection";
import TrackingInfo from "../shared/TrackingInfo";
import { ClaimStates } from "./ClaimStates";
import ClaimsConfirmationPage from "./ClaimsConfirmationPage";
import ClaimSubmitted from "./ClaimSubmitted";
import ClaimReasonSelection from "./ClaimReasonSelection";
import ClaimResolutionSelection from "./ClaimResolutionSelection";
import { useMockStep } from "../../hooks/useMockStep";
import { useTheme } from '../../context/PortalThemeContext';
import mockOrderData from '../../mock/mockOrderData.json';
import { createMockClaimFlow } from './MockClaims';
import { PiArrowLeft, PiCheck } from 'react-icons/pi';
import './ClaimsWizard.css';

const CLAIM_STEPS = [
  { key: ClaimStates.CHOOSE_CLAIM_REASON, label: 'Reason' },
  { key: ClaimStates.SELECT_CLAIM_ITEMS, label: 'Items' },
  { key: ClaimStates.CHOOSE_ISSUE, label: 'Resolution' },
  { key: ClaimStates.CLAIM_CONFIRMATION, label: 'Review' },
  { key: ClaimStates.CLAIM_SUBMITTED, label: 'Done' },
];

const ClaimsWizard = ({ initialOrderDetails, customer, trackingInfo, step, setStep, shopifyProducts, updateOrderDetails }) => {
  const [selectedClaimReason, setSelectedClaimReason] = useState(null);
  const [selectedItemsToClaim, setSelectedItemsToClaim] = useState([]);
  const [itemsToClaim, setItemsToClaim] = useState([]);
  const [reasonWithResolution, setReasonWithResolution] = useState({});
  const [orderDetails, setOrderDetails] = useState(initialOrderDetails);
  const mockStep = useMockStep();
  const theme = useTheme();

  const isClaimFlowStep = CLAIM_STEPS.some(s => s.key === step);
  const currentStepIdx = CLAIM_STEPS.findIndex(s => s.key === step);

  useEffect(() => {
    if (mockStep) {
      // Set up mock data based on the step
      if (mockStep === ClaimStates.ORDER_SUMMARY) {
        // Reset all state for order summary
        setSelectedClaimReason(null);
        setSelectedItemsToClaim([]);
        setItemsToClaim([]);
        setReasonWithResolution({});
      } else if (mockStep === ClaimStates.CHOOSE_CLAIM_REASON) {
        setSelectedClaimReason({
          name: 'DAMAGED',
          optionalText: 'Product arrived damaged during shipping'
        });
      } else if (mockStep === ClaimStates.SELECT_CLAIM_ITEMS) {
        setSelectedClaimReason({
          name: 'DAMAGED',
          optionalText: 'Product arrived damaged during shipping'
        });
        setSelectedItemsToClaim([mockOrderData.line_items[0], mockOrderData.line_items[1]]);
        setItemsToClaim([{ ...mockOrderData.line_items[0] }, { ...mockOrderData.line_items[1] }]);
      } else if (mockStep === ClaimStates.CHOOSE_ISSUE) {
        setSelectedClaimReason({
          name: 'DAMAGED',
          optionalText: 'Product arrived damaged during shipping'
        });
        setSelectedItemsToClaim([mockOrderData.line_items[0]]);
        setItemsToClaim([{ ...mockOrderData.line_items[0] }]);
        setReasonWithResolution({
          name: 'DAMAGED', optionalText: 'Product arrived damaged during shipping'
        });
      } else if (mockStep === ClaimStates.CLAIM_CONFIRMATION) {
        setSelectedClaimReason({
          name: 'DAMAGED',
          optionalText: 'Product arrived damaged during shipping'
        });
        setSelectedItemsToClaim([mockOrderData.line_items[0]]);
        setItemsToClaim([{ ...mockOrderData.line_items[0] }]);
        setReasonWithResolution({
          name: 'DAMAGED',
          optionalText: 'Product arrived damaged during shipping',
          resolution: 'Refund'
        });
      } else if (mockStep === ClaimStates.CLAIM_SUBMITTED) {
        setSelectedClaimReason({
          name: 'DAMAGED',
          optionalText: 'Product arrived damaged during shipping'
        });
        setSelectedItemsToClaim([mockOrderData.line_items[0]]);
        setItemsToClaim([{ ...mockOrderData.line_items[0] }]);
        setReasonWithResolution({
          name: 'DAMAGED', optionalText: 'Product arrived damaged during shipping',
          resolution: 'Refund'
        });
      }
    }
  }, [mockStep]); // Only depend on mockStep

  // Handle step changes separately - only when NOT in mock mode
  useEffect(() => {
    if (!mockStep) {
      if (step === ClaimStates.CHOOSE_ISSUE) {
        // When moving to CHOOSE_ISSUE, transfer selectedItemsToClaim to itemsToClaim
        if (selectedItemsToClaim.length > 0) {
          setItemsToClaim(selectedItemsToClaim.map(item => ({ ...item })));
        }
      }

      if (step === ClaimStates.CLAIM_CONFIRMATION) {
        // Clear the selection state since we now have the final items
        setSelectedItemsToClaim([]);
      }

      if (step === ClaimStates.ORDER_SUMMARY) {
        setSelectedClaimReason(null);
        setSelectedItemsToClaim([]);
        setItemsToClaim([]);
        setReasonWithResolution({});
      }
    }
  }, [step, mockStep]); // Only depend on step and mockStep, not the state variables

  const handleStartClaim = () => {
    setStep(ClaimStates.CHOOSE_CLAIM_REASON);
  };

  // Ensure we always start with the reason selection if we're not in mock mode
  useEffect(() => {
    if (!mockStep && step === ClaimStates.SELECT_CLAIM_ITEMS && !selectedClaimReason) {
      setStep(ClaimStates.CHOOSE_CLAIM_REASON);
    }
  }, [step, mockStep, selectedClaimReason, setStep]);

  const handleReasonSelected = (reason) => {
    setSelectedClaimReason(reason);
    setStep(ClaimStates.SELECT_CLAIM_ITEMS);
  };

  const handleResolutionSelected = (completeResolution) => {
    setReasonWithResolution(completeResolution);
  };

  // Move to confirmation step when reasonWithResolution is set
  useEffect(() => {
    if (reasonWithResolution && Object.keys(reasonWithResolution).length > 0 && !mockStep) {
      setStep(ClaimStates.CLAIM_CONFIRMATION);
    }
  }, [reasonWithResolution, mockStep]);

  const renderStepper = () => (
    <div className="claims-stepper">
      {CLAIM_STEPS.map((s, i) => (
        <React.Fragment key={s.key}>
          {i > 0 && (
            <div className={`stepper-line ${i <= currentStepIdx ? 'completed' : ''}`} />
          )}
          <div className="stepper-step">
            <div className={`stepper-circle ${i === currentStepIdx ? 'active' : ''} ${i < currentStepIdx ? 'completed' : ''}`}>
              {i + 1}
            </div>
            <span className={`stepper-label ${i === currentStepIdx ? 'active' : ''}`}>
              {s.label}
            </span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );

  if (isClaimFlowStep) {
    return (
      <div className="claims-flow-container">
        <button
          className="claims-back-btn"
          onClick={() => setStep(ClaimStates.ORDER_SUMMARY)}
        >
          <PiArrowLeft size={16} /> Back
        </button>

        {renderStepper()}

        {step === ClaimStates.CHOOSE_CLAIM_REASON && (
          <ClaimReasonSelection
            onReasonSelected={handleReasonSelected}
            onStepChange={setStep}
            flowType="CLAIM"
          />
        )}

        {step === ClaimStates.SELECT_CLAIM_ITEMS && (
          <ClaimsOrderSelection
            orderDetails={orderDetails}
            itemsToClaim={itemsToClaim}
            onStepChange={setStep}
            selectedItems={selectedItemsToClaim}
            setSelectedItems={setSelectedItemsToClaim}
            claimReason={selectedClaimReason}
            shopifyProducts={shopifyProducts}
            flowType="CLAIM"
          />
        )}

        {step === ClaimStates.CHOOSE_ISSUE && (
          <ClaimResolutionSelection
            selectedItems={itemsToClaim}
            claimReason={selectedClaimReason}
            onResolutionSelected={handleResolutionSelected}
            onStepChange={setStep}
            shopifyProducts={shopifyProducts}
            orderDetails={orderDetails}
            flowType="CLAIM"
          />
        )}

        {step === ClaimStates.CLAIM_CONFIRMATION && (
          <ClaimsConfirmationPage
            orderDetails={orderDetails}
            itemsToClaim={itemsToClaim}
            reasonWithResolution={reasonWithResolution}
            customer={customer}
            onStepChange={setStep}
            shopifyProducts={shopifyProducts}
            updateOrderDetails={updateOrderDetails}
            flowType="CLAIM"
          />
        )}

        {step === ClaimStates.CLAIM_SUBMITTED && (
          <ClaimSubmitted
            onStepChange={setStep}
          />
        )}
      </div>
    );
  }

  return (
    <>
      {step === ClaimStates.ORDER_TRACKING && (
        <TrackingInfo
          trackingInfo={trackingInfo}
          onBack={() => setStep(ClaimStates.ORDER_SUMMARY)}
        />
      )}
    </>
  );
};

export default ClaimsWizard;
